import { useIsFocused } from '@react-navigation/native';
import { getVerifiedPortfolioValue } from '../lib/portfolioValue';
import React, { useMemo, useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, SafeAreaView, Platform, ScrollView, Switch, ActivityIndicator } from 'react-native';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../styles/ThemeProvider';
import { useCommonStyles } from '../styles/useCommonStyles';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../types/navigation';
import { isUserAdmin } from '../utils/userPermissions';
import { getAllAchievements, Achievement } from '../utils/milestones';
import AchievementsBadges from '../components/AchievementsBadges';
import AppearanceToggle from '../components/AppearanceToggle';
import {
  getNotificationPreferences,
  toggleWeeklyDigest,
  NotificationPreferences,
} from '../lib/notificationPreferences';

export default function ProfileScreen() {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        signedOutCenter: {
          justifyContent: 'center',
          alignItems: 'center',
          paddingHorizontal: theme.spacing.xl,
        },
        header: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingVertical: theme.spacing.lg,
          paddingHorizontal: theme.spacing.lg,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        headerText: {
          ...theme.typography.h3,
          color: theme.colors.textPrimary,
        },
        container: {
          flex: 1,
          backgroundColor: theme.colors.backgroundPrimary,
          padding: theme.spacing.lg,
        },
        section: {
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          padding: theme.spacing.lg,
          marginBottom: theme.spacing.xl,
        },
        sectionTitle: {
          ...theme.typography.h4,
          color: theme.colors.textPrimary,
          marginBottom: theme.spacing.sm,
        },
        buttonContainer: {
          marginTop: 'auto',
        },
        adminButton: {
          backgroundColor: 'transparent',
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.lg,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.colors.accent,
          borderRadius: theme.radius.radiusMd,
          marginTop: theme.spacing.md,
          alignItems: 'center',
        },
        adminButtonText: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.accent,
        },
        notificationRow: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: theme.spacing.lg,
        },
        notificationInfo: {
          flex: 1,
          marginRight: theme.spacing.lg,
        },
        notificationLabel: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
          marginBottom: theme.spacing.xs,
        },
        notificationDescription: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
        },
        errorText: {
          ...theme.typography.sm,
          color: theme.colors.destructive,
          marginTop: theme.spacing.lg,
        },
      }),
    [theme],
  );
  const { session, signOut } = useAuth();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const [isAdmin, setIsAdmin] = useState(false);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const isFocused = useIsFocused();
  const [statsLoaded, setStatsLoaded] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPreferences | null>(null);
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [prefsUpdating, setPrefsUpdating] = useState(false);

  useEffect(() => {
    let active = true;
    if (session) {
      isUserAdmin().then(setIsAdmin);
      if (isFocused) fetchUserStats(() => active);
      loadNotificationPrefs();
    }
    return () => { active = false; };
  }, [session, isFocused]);

  const loadNotificationPrefs = async () => {
    setPrefsLoading(true);
    const prefs = await getNotificationPreferences();
    setNotificationPrefs(prefs);
    setPrefsLoading(false);
  };

  const handleToggleDigest = async (value: boolean) => {
    if (!notificationPrefs) return;
    setPrefsUpdating(true);
    const success = await toggleWeeklyDigest(value);
    if (success) {
      setNotificationPrefs({ ...notificationPrefs, weekly_digest_enabled: value });
    }
    setPrefsUpdating(false);
  };

  const fetchUserStats = async (active: () => boolean) => {
    try {
      if (!session) return;
      const value = await getVerifiedPortfolioValue(session.user.id);
      if (!value) throw new Error('Portfolio unavailable');
      const itemCount = value.itemCount;
      const totalValue = value.totalValue;

      const allAchievements = await getAllAchievements(itemCount, totalValue);
      if (!active()) return;
      setAchievements(allAchievements);
      setStatsLoaded(true);
    } catch (error) {
      console.error('Failed to fetch user stats:', error);
      setStatsLoaded(true);
    }
  };

  if (!session) {
    return (
      <SafeAreaView style={[commonStyles.safeArea, styles.signedOutCenter]}>
        <Text style={commonStyles.screenTitle}>Profile</Text>
        <Text style={commonStyles.screenSubtitle}>Please sign in to view your profile</Text>
        <TouchableOpacity
          style={commonStyles.primaryButton}
          onPress={() => navigation.navigate('SignIn', { redirect: 'Profile' })}
        >
          <Text style={commonStyles.primaryButtonText}>Sign In</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={commonStyles.safeArea}>
      <View style={styles.header}>
        <Text style={styles.headerText}>Profile</Text>
      </View>

      <ScrollView style={styles.container}>
        {/* Achievements Section */}
        {statsLoaded && achievements.length > 0 && (
          <AchievementsBadges achievements={achievements} />
        )}

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Appearance</Text>
          <AppearanceToggle />
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Notifications</Text>
          {prefsLoading ? (
            <ActivityIndicator size="small" color={theme.colors.accent} style={{ marginTop: theme.spacing.lg }} />
          ) : !notificationPrefs ? (
            <Text style={styles.errorText}>Unable to load preferences</Text>
          ) : (
            <View style={styles.notificationRow}>
              <View style={styles.notificationInfo}>
                <Text style={styles.notificationLabel}>Weekly Portfolio Digest</Text>
                <Text style={styles.notificationDescription}>
                  Receive a weekly email summary of your portfolio performance every Sunday
                </Text>
              </View>
              <Switch
                value={notificationPrefs.weekly_digest_enabled}
                onValueChange={handleToggleDigest}
                disabled={prefsUpdating}
                trackColor={{ false: theme.colors.backgroundElevated, true: theme.colors.accent }}
                thumbColor={theme.colors.textPrimary}
              />
            </View>
          )}
        </View>

        {Platform.OS === 'web' && isAdmin && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Admin Tools</Text>
            <TouchableOpacity
              style={styles.adminButton}
              onPress={() => navigation.navigate('AdminReports')}
            >
              <Text style={styles.adminButtonText}>📋 View Issue Reports</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.adminButton}
              onPress={() => navigation.navigate('AdminMarketDiagnostics')}
            >
              <Text style={styles.adminButtonText}>🔍 Market Pricing Diagnostics</Text>
            </TouchableOpacity>
          </View>
        )}

        <View style={styles.buttonContainer}>
          <TouchableOpacity
            style={commonStyles.destructiveButton}
            onPress={signOut}
          >
            <Text style={commonStyles.destructiveButtonText}>Sign Out</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

